import React from 'react';
import {ServiceListing} from '../constants';
import {getAssetUrl} from '../../../../utils/assets';
import {
  getInitials,
  getPrimaryServiceMeta,
  getSecondaryServiceMeta,
  getServiceDisplayName,
  getServiceIconUrl,
  servicePlaceholderImageUrl,
} from '../serviceDisplay';
import styles from '../Services.module.css';

interface ServiceCardProps {
  service: ServiceListing;
  onContactProvider: (service: ServiceListing) => void;
  onBookService: (service: ServiceListing) => void;
  onLearnMore: (service: ServiceListing) => void;
  onViewDetails: (service: ServiceListing) => void;
}

const HeartIcon: React.FC = () => (
  <svg className={styles.serviceSaveIcon} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M20.3 5.7c-1.9-1.9-5-1.9-6.9 0L12 7.1l-1.4-1.4c-1.9-1.9-5-1.9-6.9 0s-1.9 5 0 6.9L12 21l8.3-8.4c1.9-1.9 1.9-5 0-6.9Z"
      stroke="currentColor"
      strokeWidth="1.9"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ServiceMetaIcon: React.FC<{fileName: string}> = ({fileName}) => (
  <span
    className={styles.serviceMetaIcon}
    style={{'--services-meta-icon-url': `url("${getServiceIconUrl(fileName)}")`} as React.CSSProperties}
    aria-hidden="true"
  />
);

const ServiceCard: React.FC<ServiceCardProps> = ({
  service,
  onContactProvider,
  onBookService,
  onLearnMore,
  onViewDetails,
}) => {
  const [previewImageFailed, setPreviewImageFailed] = React.useState(false);
  const [avatarImageFailed, setAvatarImageFailed] = React.useState(false);
  const [isSaved, setIsSaved] = React.useState(false);
  const isCollegeService = service.serviceKind === 'college';
  const displayName = getServiceDisplayName(service);
  const profilePictureUrl = getAssetUrl(service.providerProfilePicture);
  const servicePreviewUrl = getAssetUrl(service.previewImageUrl);
  const showProfilePicture = Boolean(profilePictureUrl && !avatarImageFailed);
  const previewImageUrl = servicePreviewUrl && !previewImageFailed
    ? servicePreviewUrl
    : servicePlaceholderImageUrl;
  const primaryMeta = getPrimaryServiceMeta(service);
  const secondaryMeta = getSecondaryServiceMeta(service);
  const priceValue = service.price || 'Price on request';

  React.useEffect(() => {
    setPreviewImageFailed(false);
  }, [servicePreviewUrl]);

  React.useEffect(() => {
    setAvatarImageFailed(false);
  }, [profilePictureUrl]);

  const handleBookClick = () => {
    if (isCollegeService) {
      if (service.learnMoreUrl) {
        onLearnMore(service);
        return;
      }

      onContactProvider(service);
      return;
    }

    onBookService(service);
  };

  return (
    <article className={`card ${styles.serviceCard}`}>
      <div className={styles.serviceCardLayout}>
        <div className={styles.servicePreviewWrap}>
          <img
            src={previewImageUrl}
            alt=""
            aria-hidden="true"
            className={styles.servicePreviewImage}
            loading="lazy"
            onError={() => setPreviewImageFailed(true)}
          />
        </div>

        <div className={styles.serviceDetails}>
          <div className={styles.serviceTopRow}>
            <div className={styles.providerRow}>
              <div className={styles.avatar}>
                {showProfilePicture ? (
                  <img
                    src={profilePictureUrl}
                    alt={`${displayName} avatar`}
                    className={styles.avatarImage}
                    loading="lazy"
                    onError={() => setAvatarImageFailed(true)}
                  />
                ) : (
                  <span>{getInitials(displayName)}</span>
                )}
              </div>

              <div className={styles.providerInfo}>
                <h3 className={styles.providerName}>{displayName}</h3>
                <span className={styles.kindBadge}>{service.serviceKindLabel}</span>
              </div>
            </div>

            <button
              type="button"
              className={`${styles.serviceSaveButton} ${isSaved ? styles.serviceSaveButtonActive : ''}`.trim()}
              aria-label={`${isSaved ? 'Unsave' : 'Save'} ${service.title}`}
              aria-pressed={isSaved}
              onClick={() => setIsSaved((currentValue) => !currentValue)}
            >
              <HeartIcon />
            </button>
          </div>

          <h4 className={styles.serviceTitle}>{service.title}</h4>

          <div className={styles.serviceMetaList}>
            {primaryMeta ? (
              <span className={styles.serviceMetaItem}>
                <ServiceMetaIcon fileName="location-line.svg" />
                <span className={styles.serviceMetaText} title={primaryMeta}>{primaryMeta}</span>
              </span>
            ) : null}
            {secondaryMeta ? (
              <span className={styles.serviceMetaItem}>
                <ServiceMetaIcon fileName="clock-line.svg" />
                <span className={styles.serviceMetaText} title={secondaryMeta}>{secondaryMeta}</span>
              </span>
            ) : null}
          </div>

          <div className={styles.serviceBottomRow}>
            <div className={styles.priceBlock}>
              <div className={styles.priceValue}>{priceValue}</div>
            </div>

            <div className={styles.serviceActionGroup}>
              <button
                type="button"
                className={styles.viewDetailsButton}
                onClick={() => onViewDetails(service)}
              >
                View details
              </button>
              {isCollegeService && service.learnMoreUrl ? (
                <a
                  href={service.learnMoreUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.bookNowButton}
                  onClick={handleBookClick}
                >
                  Book now
                </a>
              ) : (
                <button
                  type="button"
                  className={styles.bookNowButton}
                  onClick={handleBookClick}
                >
                  Book now
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </article>
  );
};

export default React.memo(ServiceCard);
